import React from 'react';
import {
  NotificationsActiveOutlined,
  BoltOutlined,
  DescriptionOutlined,
} from '@mui/icons-material';
import type { ActionLogEntry } from '../../../types/backend';

export { formatRelativeTime } from './eventDisplay';

/**
 * Human-facing presentation of OpsItems behind the action log.
 *
 * One OpsItem is one incident (an alert that became tracked work, the result of
 * an automation rule, or a rendered report). The list endpoint exposes a thin
 * slice of the OpsItem, so these helpers derive the *kind* of incident and a
 * normalised severity from the fields we actually have — never inventing the
 * absent `audit` type the old dropdown advertised.
 */

export type OpsItemKind = 'alert' | 'automation' | 'report';

export interface KindMeta {
  key: OpsItemKind;
  label: string;
  Icon: typeof NotificationsActiveOutlined;
  color: string;
}

export const KIND_META: Record<OpsItemKind, KindMeta> = {
  alert: { key: 'alert', label: 'Alert', Icon: NotificationsActiveOutlined, color: 'warning.main' },
  automation: { key: 'automation', label: 'Automation', Icon: BoltOutlined, color: 'info.main' },
  report: { key: 'report', label: 'Report', Icon: DescriptionOutlined, color: 'primary.main' },
};

/** The real incident kind, derived from fields the list endpoint actually returns. */
export function opsItemKind(e: ActionLogEntry): OpsItemKind {
  if (e.report_ref || e.rule_type === 'report') return 'report';
  if (e.rule_type === 'automation') return 'automation';
  return 'alert';
}

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'none';

export interface SeverityMeta {
  level: SeverityLevel;
  label: string;
}

const SEVERITY_BY_CODE: Record<string, SeverityMeta> = {
  '1': { level: 'critical', label: 'Critical' },
  '2': { level: 'high', label: 'High' },
  '3': { level: 'medium', label: 'Medium' },
  '4': { level: 'low', label: 'Low' },
};

export const SEVERITY_ORDER: SeverityLevel[] = ['critical', 'high', 'medium', 'low', 'none'];

export const SEVERITY_LABEL: Record<SeverityLevel, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', none: 'No severity',
};

export function severityMeta(severity: string): SeverityMeta {
  return SEVERITY_BY_CODE[severity] ?? { level: 'none', label: severity ? `Sev ${severity}` : 'Unknown' };
}

export const STATUS_ORDER = ['Open', 'InProgress', 'Resolved'];

export const STATUS_LABEL: Record<string, string> = {
  Open: 'Open', InProgress: 'In progress', Resolved: 'Resolved',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/** Whether this entry surfaced any agent automation output. */
export function hasAutomation(e: ActionLogEntry): boolean {
  return Boolean(e.automation_result && e.automation_result.result_summary);
}

interface KindIconProps {
  kind: OpsItemKind;
  size?: number;
}

export const KindIcon: React.FC<KindIconProps> = ({ kind, size = 18 }) => {
  const { Icon, color } = KIND_META[kind];
  return <Icon sx={{ fontSize: size, color }} />;
};
